import { motion } from 'framer-motion';
import { Heart, Users, Clock, Award, Coffee, Utensils } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const About = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stats = [
    { number: 'Open Daily', label: '11:45am-10pm', icon: <Clock className="w-6 h-6" /> },
    { number: '1000+', label: 'Happy Customers', icon: <Users className="w-6 h-6" /> },
    { number: '100+', label: 'Menu Items', icon: <Utensils className="w-6 h-6" /> },
    { number: '100%', label: 'Fresh Ingredients', icon: <Heart className="w-6 h-6" /> },
  ];

  const values = [
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Made with Love',
      description: 'Every dish is prepared fresh with quality ingredients and a lot of care.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Student First',
      description: 'We understand student life - affordable prices, quick service, and convenient hours.'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Quality Promise',
      description: 'No compromises on taste or hygiene. Your satisfaction is our priority.'
    },
  ];

  return (
    <div className="page-wrapper pt-24" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 px-6 md:px-12 bg-[#F0EBE0]" data-testid="about-hero">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-accent text-2xl text-[#E07A5F] block mb-2">
                Our Story
              </span>
              <h1 
                className="text-4xl md:text-6xl font-black text-[#4A3B32] mb-6"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                About A1 Cafe
              </h1>
              <p className="!text-lg mb-6 text-[#6D5D53] leading-relaxed">
                Located in the heart of the NITK hostel area, A1 Cafe has become a go-to spot for students looking to unwind, refuel, and connect. Known for its wide variety of snacks, beverages, and comfort food, we've built a space that fits perfectly into everyday campus life.
              </p>
              <p className="text-[#6D5D53] leading-relaxed">
                From late-night coffee runs during exam season to celebrating project submissions, 
                A1 Cafe has been there for every moment. We understand what students need – 
                good food, fair prices, and a warm place to chill.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_f7742170-5b8f-447f-9d8a-e99e46f1b0c0/artifacts/7gppsnng_unnamed%202%20%281%29.webp"
                  alt="A1 Cafe - NITK Campus"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#E07A5F] rounded-2xl -z-10" />
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#F2CC8F] rounded-full -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#4A3B32]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="text-center"
              >
                <div className="w-14 h-14 bg-[#E07A5F] rounded-2xl flex items-center justify-center mx-auto mb-4 text-white">
                  {stat.icon}
                </div>
                <div className="md:text-5xl font-black !text-4xl mb-2 text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                  {stat.number}
                </div>
                <div className="!font-medium text-white/70">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#F9F7F2]" data-testid="mission-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <div className="aspect-square rounded-3xl overflow-hidden shadow-xl">
                <img 
                  src="https://res.cloudinary.com/dql6llktr/image/upload/w_600,h_600,c_fill,q_auto,f_auto/v1770374622/WhatsApp_Image_2026-02-06_at_16.13.02_nyfnob.jpg"
                  alt="Coffee being prepared"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <span className="font-accent text-2xl text-[#E07A5F] block mb-2">
                What We Believe
              </span>
              <h2 
                className="text-3xl md:text-4xl font-bold text-[#4A3B32] mb-6"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                Our Mission
              </h2>
              <p className="text-lg text-[#6D5D53] mb-6 leading-relaxed">
                To serve quality food at prices that don't burn a hole in student pockets. 
                We believe everyone deserves a good meal, especially during those long study sessions.
              </p>
              <div className="bg-[#F0EBE0] rounded-2xl p-6 border-l-4 border-[#E07A5F]">
                <p className="text-[#4A3B32] italic text-lg font-accent text-2xl">
                  "Quality food at student-friendly prices – that's the A1 promise."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#F0EBE0]" data-testid="values-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="font-accent text-2xl text-[#E07A5F] block mb-2">
              Why Choose Us
            </span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#4A3B32]"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Our Values
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-white rounded-3xl p-8 text-center shadow-sm hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-16 h-16 bg-[#E07A5F] rounded-2xl flex items-center justify-center mx-auto mb-6 text-white">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-[#4A3B32] mb-3" style={{ fontFamily: 'Fraunces, serif' }}>
                  {value.title}
                </h3>
                <p className="text-[#6D5D53]">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Available on Platforms */}
      <section className="py-12 px-6 md:px-12 bg-[#F9F7F2]" data-testid="platforms-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#6D5D53] mb-6 font-medium">Order from your favorite platform:</p>
          <div className="flex items-center justify-center gap-6">
            <a 
              href="https://www.swiggy.com/menu/1242857?source=sharing" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white rounded-xl px-6 py-4 shadow-md hover:shadow-lg hover:scale-105 transition-all"
            >
              <img src="https://customer-assets.emergentagent.com/job_f7742170-5b8f-447f-9d8a-e99e46f1b0c0/artifacts/dfsl17ui_images.jfif" alt="Swiggy" className="h-8 w-8 object-contain" />
            </a>
            <a 
              href="https://zomato.onelink.me/xqzv/rcql1v1i" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white rounded-xl px-6 py-4 shadow-md hover:shadow-lg hover:scale-105 transition-all"
            >
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/75/Zomato_logo.png/200px-Zomato_logo.png" alt="Zomato" className="h-8" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#F9F7F2]" data-testid="about-cta">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-3xl p-8 md:p-12 shadow-lg"
          >
            <Coffee className="w-16 h-16 text-[#E07A5F] mx-auto mb-6" />
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#4A3B32] mb-4"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Come Say Hi!
            </h2>
            <p className="text-lg text-[#6D5D53] mb-8 max-w-lg mx-auto">
              We're always happy to see familiar and new faces. Drop by for a coffee, 
              study with friends, or just hang out!
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/menu" data-testid="about-view-menu-btn">
                <Button className="btn-primary rounded-full px-8 py-4 text-lg font-bold">
                  View Our Menu
                </Button>
              </Link>
              <Link to="/contact" data-testid="about-contact-btn">
                <Button className="btn-secondary rounded-full px-8 py-4 text-lg font-bold">
                  Get Directions
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
